import React from 'react';
import { useTranslation } from 'react-i18next';

// ── The grey card every not-yet-mounted submenu shows (owner 2026-10-13) ────────────────────────
// One component, three states with their own copy, because they ask three different things of the
// reader: (a) wait for a later version; (b) ask an administrator for a module permission — and the
// pane behind it is never mounted, so the gated API is never called and never 403s; (c) create the
// company's invoicing-portal account first, because the data simply does not exist without it.
export type PlaceholderState = 'soon' | 'perm' | 'noportal';

export default function Placeholder({ title, state, module, copy, link, children }: {
  title: string;
  state: PlaceholderState;
  module?: string;                                    // (b): the missing module key(s), shown as-is
  copy?: string;                                      // overrides the state's line (e.g. "Thai tax documents do not apply")
  link?: { label: string; href?: string; onClick?: () => void };   // optional secondary action, new tab for hrefs
  children?: React.ReactNode;                         // optional extras under the copy (e.g. ledger PDF buttons)
}) {
  const { t } = useTranslation();
  const line = copy || (state === 'perm' ? t('aw_ph_perm', { module: module || '' }) : state === 'noportal' ? t('aw_ph_noportal') : t('aw_ph_soon'));
  const icon = state === 'perm' ? 'ti ti-lock' : state === 'noportal' ? 'ti ti-building-store' : 'ti ti-hourglass-empty';
  return (
    <div style={{ background: 'var(--c-gray-50)', border: '1px solid var(--border)', borderRadius: 10, padding: '18px 20px', color: 'var(--text-tertiary)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 6 }}>
        <i className={icon} style={{ fontSize: 18 }} aria-hidden />
        <div style={{ fontSize: 14, fontWeight: 800 }}>{title}</div>
      </div>
      <div style={{ fontSize: 12.5, lineHeight: 1.6 }}>{line}</div>
      {link && (
        <div style={{ marginTop: 10 }}>
          {link.href
            ? <a href={link.href} target="_blank" rel="noreferrer" style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--accent)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                {link.label} <i className="ti ti-external-link" style={{ fontSize: 12 }} aria-hidden />
              </a>
            : <button onClick={link.onClick} style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--accent)', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}>{link.label}</button>}
        </div>
      )}
      {children && <div style={{ marginTop: 12 }}>{children}</div>}
    </div>
  );
}
